import React, { useCallback, useMemo, useRef, useState } from "react";
import { useExperimentsById } from "@capone/experiments";
import { Box, Popover, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLink,
  ActionLinks,
  IActionLink,
  Icon,
  IconName,
  MyTripsCard,
  TripInfoDetails,
  getTwoLineAddresses,
  useDeviceTypes,
  CorporateButton,
} from "halifax";
import {
  CFAR_CONTRACT_TYPE,
  HOTELS_CLICKED_CHANGE_RESERVATION,
  DatelessSearchSource,
  HotelBookAgainSourceEnum,
  HotelConfirmationCodeEnum,
  HotelItinerary,
  HOTEL_BOOK_AGAIN,
  IOpenModal,
  ItemizedReceiptEventsProperties,
  ItineraryEnum,
  ItineraryWithType,
  LodgingAddressExact,
  LodgingCollectionEnum,
  MyTripsFilter,
  MyTripsModalTypes,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  Reservation,
  transformToShopDetailsStringifiedQuery,
  VIEWED_ITEMIZED_RECEIPT,
} from "redmond";
import "./styles.scss";

import {
  isCorpTenant,
  useMultiroomCountText,
} from "@capone/common";
import { useHistory, Link } from "react-router-dom";
import { config } from "../../../../../../../../api/config";
import { trackEvent } from "../../../../../../../../api/v1/analytics/trackEvent";
import {
  ActiveExperiments,
  useExperiment,
} from "../../../../../../../../context/experiments";
import {
  calculateHotelTotals,
  getCurrencyString,
} from "../../../../../../../../utils/helpers";
import { getBookAgainUrl } from "../../../../../../../../utils/bookingHelpers";
import {
  DESKTOP_OFFSET_SCROLL,
  HOW_REFUNDS_WORK_LINK,
  HOW_REFUNDS_WORK_TEXT,
  MOBILE_OFFSET_SCROLL,
} from "../../../../constants";
import { AddOn, AddOnEnum, AddOnsSummary } from "../../../AddOnsSummary";
import { ConfirmationSummary } from "../../../ConfirmationSummary";
import { getLabels, getValues } from "../../../FlightCard/helpers";
import { PaymentSummary } from "../../../PaymentSummary";
import { SummaryCard } from "../../../SummaryCard";
import { TravelersSummary } from "../../../TravelersSummary";
import * as textConstants from "../../constants";
import { HotelCancellation, HotelCardRoomTypeContent } from "../HotelInfoParts";
import { HotelCheckInInstructions } from "../HotelInfoParts/component";
import { HotelMap } from "../HotelMap";
import { DesktopHotelCardConnectorProps } from "./container";
import { TripDescriptionSummary } from "../../../TripDescriptionSummary";
import {
  hasCfarExpired,
  isNegativePremiumCfar,
  shouldShowCfarActionButton,
} from "../../helpers/cfarHelpers";

interface IDesktopHotelCardProps extends DesktopHotelCardConnectorProps {
  expandedCard: string;
  hotel: HotelItinerary;
  isPastTrips?: boolean;
  banner?: JSX.Element;
  confirmationCodeClassName?: string;
  isCancel?: boolean;
  isMobile?: boolean;
  onExpandCard: (cardId: string) => void;
  setOpenModal: (modalType: IOpenModal) => void;
  travelerNames?: string[];
  tripsFilter: MyTripsFilter;
  moreActions?: IActionLink[];
}

export const addHotelType = (hotel: HotelItinerary): ItineraryWithType => ({
  ...hotel,
  type: ItineraryEnum.Hotel,
});

export const getPostalCode = (reservation: Reservation): string => {
  const { lodgingData } = reservation;
  const city = lodgingData?.city ? `${lodgingData.city},` : "";
  const state = lodgingData?.state?.code?.trim()
    ? `${lodgingData.state.code},`
    : "";
  return `${city} ${state} ${lodgingData?.country || ""}`;
};

export const DesktopHotelCard = (props: IDesktopHotelCardProps) => {
  const {
    expandedCard,
    hotel,
    isMobile,
    onExpandCard,
    setOpenModal,
    banner,
    isCancel,
    isPastTrips = false,
    confirmationCodeClassName,
    travelerNames,
    tripsFilter,
    moreActions = [],
  } = props;
  const {
    reservation,
    paymentAmountInfo,
    paymentBreakdown,
    ancillaries,
    tenant,
    tripDescription,
  } = hotel;

  // note: destructuring can be expanded in the future as we have more ancillaries
  const { cfar } = ancillaries;

  const { calculatedTripTotal, refundableAmount } = calculateHotelTotals({
    reservation,
    paymentBreakdown,
    cfar,
  });
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  const itemizedReceiptExp = useExperiment(
    ActiveExperiments.SelfServeHotelItemizedReceipt,
  );
  const isCorpHotelItemizedReceiptsEnabled =
    useExperimentsById("corp-traveler-hotel-itemized-receipt")?.variant ===
    "available";

  const hotelBookAgainEnabled =
    useExperimentsById("corp-hotel-book-again")?.variant === "available";

  const isExpenseManagementEnabled =
    useExperimentsById("corp-expense-management")?.variant === "available";

  const refundPopoverRef = useRef<HTMLButtonElement>(null);
  const [refundPopoverOpen, setRefundPopoverOpen] = useState(false);

  const hotelAddress = reservation.lodgingData?.address as
    | LodgingAddressExact
    | undefined;

  // parse addres to only show the street, this could lead to potential bugs
  // because BE returns everything as a full address i.e: "15 Beacon St, Boston, MA, US"
  const hotelStreet = getTwoLineAddresses(hotelAddress).line1;

  const closeRefundPopover = () => setRefundPopoverOpen(false);

  const onOpenModal = (type: MyTripsModalTypes) =>
    setOpenModal({
      type,
      selectedItinerary: addHotelType(hotel),
    });

  const openRefundPopover = () => setRefundPopoverOpen(true);

  const showCancelButton = !isPastTrips;

  const isDatelessSearchExperiment = useExperiment(
    ActiveExperiments.LUXURY_DATELESS_SEARCH_EXPERIMENT,
  );

  const forceCfarErrorModalEnabled = useExperiment(
    "corp-force-cfar-error-modal",
  );

  const hasCfar = cfar != null;
  const isNegativePremium = isNegativePremiumCfar(cfar);

  const { roomInfo, count: roomsCount } = hotel.reservation.bookedRooms[0];
  const tripCardSubtitle = isCorpTenant(config.TENANT) ? (
    <Box>
      <Typography className="info-container" variant="body1">
        {hotel.reservation.lodgingData?.name}
      </Typography>
      <Typography variant="caption" color="inherit">
        {roomInfo.name} {useMultiroomCountText(roomsCount)}
      </Typography>
    </Box>
  ) : (
    hotel.reservation.lodgingData?.name
  );

  // note: hasAddOnToDisplay can be expanded in the future as we have more features added in
  const hasAddOnToDisplay = hasCfar && !isNegativePremium;

  const shouldOpenErrorModal =
    forceCfarErrorModalEnabled && !!tenant && config.TENANT !== tenant;

  // As we'll only be showing the CFAR action button for expired CFAR policies if they are negative premium CFAR policies,
  // we will only open the expired modal those three conditions are met.
  // With HTS CFARs, expired contracts will be handled in the iframe
  const isLegacyContract =
    cfar?.contractType?.ContractType !== CFAR_CONTRACT_TYPE.HTS;
  const shouldOpenExpiredModal =
    isLegacyContract && hasCfarExpired(cfar) && isNegativePremium;

  const handleOpenCfar = useCallback(() => {
    if (shouldOpenErrorModal) {
      onOpenModal(MyTripsModalTypes.CfarHotelError);
    } else if (shouldOpenExpiredModal) {
      onOpenModal(MyTripsModalTypes.CfarHotelExpired);
    } else {
      onOpenModal(MyTripsModalTypes.CfarHotel);
    }
  }, [onOpenModal, shouldOpenErrorModal, shouldOpenExpiredModal]);

  const showCfarButton = shouldShowCfarActionButton(cfar);

  const actions = useMemo(() => {
    const actionsArr: IActionLink[] = [];

    if (!isCancel && showCancelButton) {
      actionsArr.push({
        content: "Change hotel reservation",
        onClick: () => {
          trackEvent({
            eventName: HOTELS_CLICKED_CHANGE_RESERVATION,
            properties: {
              reservation_id: hotel?.reservation?.reservationId,
              reservation_booking_id: hotel?.reservation?.reservationBookingId,
              lodging_id: hotel?.reservation?.lodgingData.id,
              check_in_date: hotel?.reservation?.checkInDate,
            },
            encryptedProperties: [
              hotel?.reservation?.trackingPropertiesV2.encryptedProperties ??
              "",
            ],
          });
          onOpenModal(MyTripsModalTypes.ChangeHotel);
        },
      });

      if (showCfarButton) {
        actionsArr.push({
          linkClassName: "desktop-cfar-link",
          content: (
            <>
              <Icon className="cfar-icon" name={IconName.CheckShieldBlue} />
              {textConstants.CANCEL_FOR_ANY_REASON}
            </>
          ),
          onClick: handleOpenCfar,
          disabled: isCancel,
        });
      } else {
        actionsArr.push({
          content: "Cancel hotel reservation",
          onClick: () => onOpenModal(MyTripsModalTypes.CancelHotel),
        });
      }
    } else if (isCancel) {
      actionsArr.push({
        content: (
          <>
            {HOW_REFUNDS_WORK_LINK}
            <Icon className="info-icon" name={IconName.InfoCircle} />
          </>
        ),
        linkClassName: "how-refunds-work-link",
        linkRef: refundPopoverRef,
        onClick: openRefundPopover,
      });
    }

    actionsArr.push({
      content: "Resend confirmation",
      onClick: () => onOpenModal(MyTripsModalTypes.ResendConfirmation),
    });

    if (
      !isCancel &&
      (itemizedReceiptExp || isCorpHotelItemizedReceiptsEnabled)
    ) {
      actionsArr.push({
        content: "View itemized receipt",
        onClick: () => {
          onOpenModal(MyTripsModalTypes.ItemizedHotelReceipt);
          trackEvent({
            eventName: VIEWED_ITEMIZED_RECEIPT,
            properties: {
              trip_id: hotel.reservation.reservationId,
              travel_type: "hotel",
              active_tab: tripsFilter,
              reservation_booking_id: hotel.reservation.reservationBookingId,
            } satisfies ItemizedReceiptEventsProperties,
          });
        },
      });
    }

    if (
      isDatelessSearchExperiment &&
      (reservation.lodgingCollection === LodgingCollectionEnum.Premier ||
        reservation.lodgingCollection === LodgingCollectionEnum.Lifestyle) &&
      reservation.lodgingData != null
    ) {
      actionsArr.push({
        content: "View hotel details",
        onClick: () => {
          history.push({
            pathname: PATH_PREMIER_COLLECTION_SHOP_DETAILS,
            search: transformToShopDetailsStringifiedQuery({
              lodgingId: reservation.lodgingData.id,
              source: DatelessSearchSource.MyTrips,
            }),
          });
        },
      });
    }

    actionsArr.push(...moreActions);

    return actionsArr;
  }, [
    isCancel,
    isCorpHotelItemizedReceiptsEnabled,
    itemizedReceiptExp,
    refundPopoverRef,
    openRefundPopover,
    showCancelButton,
    showCfarButton,
    handleOpenCfar,
    moreActions,
  ]);

  return (
    <Box
      id={reservation.reservationId}
      key={reservation.reservationId}
      className={clsx("hotel-trip-container", {
        mobile: isMobile,
        expanded: expandedCard === reservation.reservationId,
        "premier-collection":
          reservation.lodgingCollection === LodgingCollectionEnum.Premier,
      })}
    >
      {reservation.lodgingCollection === LodgingCollectionEnum.Premier && (
        <Icon name={IconName.PremierCollectionRibbon} />
      )}
      {reservation.lodgingCollection === LodgingCollectionEnum.Lifestyle && (
        <Icon name={IconName.LifestyleCollectionRibbon} />
      )}
      <MyTripsCard
        className="trip-card-title"
        banner={banner}
        content={
          <TripInfoDetails
            hideTitleTag={isCancel}
            confirmationCodeClassName={confirmationCodeClassName}
            titles={{
              confirmationLabel: textConstants.CONFIRMATION,
              startLabel: `${textConstants.CHECK_IN}:`,
              endLabel: `${textConstants.CHECK_OUT}:`,
              postalCode: getPostalCode(reservation),
              phoneNum: reservation.lodgingData?.phone || "",
              streetAddress: hotelStreet,
              ...textConstants.getHotelInfoDetails(hotel),
              subtitle: hotel.reservation.lodgingData?.name
                ? tripCardSubtitle
                : "",
            }}
          />
        }
        actions={<ActionLinks actions={actions} />}
        expandString={
          expandedCard === reservation.reservationId
            ? textConstants.VIEW_LESS
            : textConstants.VIEW_DETAILS
        }
        expandIcon={
          expandedCard === reservation.reservationId
            ? IconName.MinusBlueCircle
            : IconName.PlusBlueCircle
        }
        isExpanded={expandedCard === reservation.reservationId}
        topRightAction={null}
        onExpandClick={() => {
          onExpandCard(reservation.reservationId);
          setTimeout(() => {
            const OFFSET = matchesMobile
              ? MOBILE_OFFSET_SCROLL
              : DESKTOP_OFFSET_SCROLL;
            const cardTop =
              document
                ?.getElementById(reservation.reservationId)
                ?.getBoundingClientRect().top || 0;
            window.scrollBy({
              top: (cardTop as number) - OFFSET,
              behavior: matchesMobile ? "auto" : "smooth",
            });
          }, 100);
        }}
        travelers={travelerNames}
      />
      {reservation.reservationId === expandedCard && (
        <Box className="hotel-desktop-expanded-container">
          <Box className="hotel-summary-container">
            <HotelMap lodging={hotel.reservation.lodgingData} />
            <HotelCardRoomTypeContent
              bookedRooms={hotel.reservation.bookedRooms}
              isLuxuryCollection={
                hotel.reservation.lodgingCollection ===
                LodgingCollectionEnum.Premier
              }
            />
            <HotelCancellation
              hotelCfar={hotel.ancillaries.cfar}
              cancellationPolicy={hotel.reservation.cancellationPolicy}
              cancellationPolicyV2={hotel.reservation.cancellationPolicyV2}
              refundAmount={refundableAmount}
            />
            {hotel.reservation.checkInDate &&
              hotel.reservation.checkOutDate && (
                <HotelCheckInInstructions
                  fromDate={hotel.reservation.checkInDate}
                  untilDate={hotel.reservation.checkOutDate}
                  checkInInstructions={
                    hotel.reservation.lodgingData.checkInInstructions
                  }
                />
              )}
            {isCorpTenant(config.TENANT) && hotelBookAgainEnabled && (
              <Box
                style={{
                  gridColumn: "2",
                  gridRow: "4",
                  display: "flex",
                  justifyContent: "flex-start",
                  marginTop: "16px",
                  marginLeft: "20px",
                }}
              >
                <Link
                  to={getBookAgainUrl(
                    hotel.reservation,
                    HotelBookAgainSourceEnum.MyTrips,
                  )}
                  style={{ textDecoration: "none" }}
                  aria-label="Book this hotel again"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent({
                      eventName: HOTEL_BOOK_AGAIN,
                      properties: {
                        source: HotelBookAgainSourceEnum.MyTrips,
                      },
                    });
                  }}
                >
                  <CorporateButton
                    variant="outlined"
                    icon={<Icon name={IconName.CorpRedoArrow} />}
                    style={{ width: "auto" }}
                  >
                    Book hotel again
                  </CorporateButton>
                </Link>
              </Box>
            )}
          </Box>
          <Box
            className={clsx("summary-info-container", {
              "two-column-view": hasAddOnToDisplay,
              "with-trip-description":
                isExpenseManagementEnabled && Boolean(tripDescription),
            })}
          >
            <SummaryCard
              className="travellers-summary"
              action={
                <ActionLink
                  className="details-link"
                  onClick={() => onOpenModal(MyTripsModalTypes.TravelersModal)}
                  content="Details"
                />
              }
            >
              <TravelersSummary
                label="Guest information"
                travelers={reservation.guests
                  .map((person) => person?.givenName)
                  .join(", ")}
              />
            </SummaryCard>
            <SummaryCard
              className="payment-summary"
              action={
                <ActionLink
                  className="details-link"
                  onClick={() => onOpenModal(MyTripsModalTypes.PaymentModal)}
                  content="Details"
                />
              }
            >
              <PaymentSummary
                tripTotalAmount={getCurrencyString({
                  currencyCode: calculatedTripTotal.currencyCode,
                  currencySymbol: calculatedTripTotal.currencySymbol,
                  value: calculatedTripTotal.value,
                })}
                cardLabel={getLabels(paymentAmountInfo).cardLabel}
                cardAmount={getValues(paymentAmountInfo).cardValue}
                rewardsLabel={getLabels(paymentAmountInfo).rewardLabel}
                rewardsAmount={getValues(paymentAmountInfo).rewardValue}
                showCardLabel={Boolean(getLabels(paymentAmountInfo).cardLabel)}
                showRewardsLabel={Boolean(
                  getLabels(paymentAmountInfo).rewardLabel,
                )}
              />
            </SummaryCard>
            <SummaryCard
              className="confirmation-summary"
              action={
                hotel.reservation.hotelConfirmationCode &&
                  hotel.reservation.hotelConfirmationCode.HotelConfirmationCode ===
                  HotelConfirmationCodeEnum.HotelConfirmationConfirmed ? (
                  <ActionLink
                    className="details-link"
                    onClick={() =>
                      onOpenModal(MyTripsModalTypes.ConfirmationModal)
                    }
                    content="View all (2)"
                  />
                ) : null
              }
            >
              <ConfirmationSummary
                confirmationCode={`H-${reservation.reservationId}`}
              />
            </SummaryCard>
            {hasAddOnToDisplay ? (
              <SummaryCard className="add-ons-summary">
                <AddOnsSummary
                  addOns={(() => {
                    const addOnItems: AddOn[] = [];
                    if (hasCfar) {
                      addOnItems.push({
                        expiryDate: cfar?.expired ?? "",
                        expiredTz: cfar?.expiredTz ?? "America/New_York",
                        onClick: handleOpenCfar,
                        isEnabled: !hasCfarExpired(cfar) && !isCancel,
                        type: AddOnEnum.Cfar,
                      });
                    }
                    return addOnItems;
                  })()}
                />
              </SummaryCard>
            ) : null}
            {isExpenseManagementEnabled && tripDescription && (
              <SummaryCard className="trip-description-summary">
                <TripDescriptionSummary tripDescription={tripDescription} />
              </SummaryCard>
            )}
          </Box>
        </Box>
      )}
      {isCancel && (
        <Popover
          anchorEl={refundPopoverRef.current}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          className="how-refunds-work-popover"
          onClose={closeRefundPopover}
          open={refundPopoverOpen}
        >
          <Typography className="subtitle">{HOW_REFUNDS_WORK_TEXT}</Typography>
        </Popover>
      )}
    </Box>
  );
};
